import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { PHONE, PHONE_TEL } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EnquiryForm = ({
  formTestId,
  submitTestId,
  fields,
  submitLabel,
  service,
  serviceField,
  initialValues = {},
  customValidate,
  successTitle = "Enquiry Sent Successfully",
}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const setVal = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    fields.forEach((f) => {
      const v = (values[f.name] ?? "").toString().trim();
      if (f.required && !v) next[f.name] = `${f.label} is required`;
      else if (f.type === "tel" && v && !/^[6-9]\d{9}$/.test(v.replace(/[\s-]/g, "")))
        next[f.name] = "Enter a valid 10-digit mobile number";
      else if (f.type === "number" && v && Number(v) < 1) next[f.name] = "Must be at least 1";
    });
    if (customValidate) Object.assign(next, customValidate(values));
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const details = {};
      fields.forEach((f) => {
        if (!["name", "phone", "message"].includes(f.name)) details[f.name] = values[f.name] ?? "";
      });
      await axios.post(`${API}/enquiries`, {
        name: values.name,
        phone: values.phone,
        message: values.message || "",
        service: serviceField
          ? String(values[serviceField] || "general").toLowerCase().replace(/\s+/g, "-")
          : service,
        details,
      });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid={`${formTestId}-success`}
        className="rounded-md border border-gold/40 bg-white p-8 text-center shadow-sm"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-gold" />
        <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">{successTitle}</h3>
        <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink/70">
          Thank you, {values.name}. Our team has received your enquiry and will call you shortly at{" "}
          <span className="font-semibold text-navy">{values.phone}</span>. For immediate assistance,
          call us at{" "}
          <a href={PHONE_TEL} className="font-semibold text-gold hover:text-gold-hover">
            {PHONE}
          </a>
          .
        </p>
        <button
          type="button"
          data-testid={`${formTestId}-send-another`}
          onClick={() => {
            setValues({});
            setStatus("idle");
          }}
          className="mt-6 inline-flex items-center justify-center rounded-sm border border-navy/20 px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-navy transition-colors hover:border-gold hover:text-gold"
        >
          Send Another Enquiry
        </button>
      </div>
    );
  }

  return (
    <form data-testid={formTestId} onSubmit={handleSubmit} noValidate className="grid gap-5 sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
          <label htmlFor={`${formTestId}-${f.name}`} className="input-label">
            {f.label}
            {f.required && <span className="text-gold"> *</span>}
          </label>
          {f.type === "select" ? (
            <select
              id={`${formTestId}-${f.name}`}
              data-testid={f.testid}
              value={values[f.name] ?? ""}
              onChange={(e) => setVal(f.name, e.target.value)}
              className="input-field"
            >
              <option value="">Select {f.label}</option>
              {f.options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : f.type === "textarea" ? (
            <textarea
              id={`${formTestId}-${f.name}`}
              data-testid={f.testid}
              rows={4}
              value={values[f.name] ?? ""}
              onChange={(e) => setVal(f.name, e.target.value)}
              placeholder={f.placeholder}
              className="input-field resize-none"
            />
          ) : (
            <input
              id={`${formTestId}-${f.name}`}
              data-testid={f.testid}
              type={f.type}
              min={f.type === "number" ? 1 : undefined}
              value={values[f.name] ?? ""}
              onChange={(e) => setVal(f.name, e.target.value)}
              placeholder={f.placeholder}
              className="input-field"
            />
          )}
          {errors[f.name] && (
            <p data-testid={`${f.testid}-error`} className="mt-1.5 text-xs font-medium text-red-600">
              {errors[f.name]}
            </p>
          )}
        </div>
      ))}

      {status === "error" && (
        <p
          data-testid={`${formTestId}-error`}
          className="flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:col-span-2"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          Something went wrong while sending. Please call us at {PHONE} or try again.
        </p>
      )}

      <div className="sm:col-span-2">
        <button
          type="submit"
          data-testid={submitTestId}
          disabled={status === "sending"}
          className="btn-gold w-full disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
          {status === "sending" ? "Sending..." : submitLabel}
        </button>
      </div>
    </form>
  );
};
